import React, {useContext, useEffect, useState} from "react";
import useAxios from "../../utils/useAxios";
import {useTranslation} from "react-i18next";
import {useInView} from "react-intersection-observer";
import {Container} from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import FeatherIcon from "feather-icons-react";
import {CustomModal} from "../../components/CustomModel/CustomModal";
import useModal from "../../hooks/useModal";
import MenuList from "../../components/MenuList";
import PlayerForm from "./PlayerForm";
import {useHistory, useLocation, useParams} from "react-router-dom";
import {COUNTRY_OPTIONS, LEVEL_OPTIONS} from "../../constants";
import debounce from "lodash.debounce";
import { useMemo } from "react";
import {getOptionName} from "../../utils/getOptionName";
import AuthContext from "../../contexts/AuthContext";
import PlayerMergeForm from "./PlayerMergeForm";
import { useTheme } from "../../contexts/ThemeContext";
import { CompactTableRow } from "../../components/CompactTableRow";
import { cacheBustUrl } from "../../utils/cacheBustUrl";

const PlayersList = () => {
    const {t} = useTranslation();
    const api = useAxios();
    const {modalIsOpen, setIsOpen, openModal} = useModal(false);
    const {ref, inView} = useInView({threshold: 1});
    const { compactTable } = useTheme();
    const [players, setPlayers] = useState([]);
    const [currentPage, setCurrentPage] = useState("");
    const [countPlayers, setCountPlayers] = useState();
    const [loader, setLoader] = useState(false);
    const [skeletonCards, setSkeletonCards] = useState(10);
    const [editingPlayer, setEditingPlayer] = useState(null);
    const {id: baseId} = useParams();
    const [isSearching, setIsSearching] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [lastQuery, setLastQuery] = useState("");
    let {user} = useContext(AuthContext)

    const history = useHistory();
    const location = useLocation();
    const [pageTitle, setPageTitle] = useState(location.state?.baseName || "");
    const [canWrite, setCanWrite] = useState(location.state?.canWrite || false);

    const [mergePlayer, setMergePlayer] = useState(null);
    const { modalIsOpen: mergeModalIsOpen, setIsOpen: setMergeModalIsOpen, openModal: openMergeModal } = useModal(false);

    if (!baseId) history.replace("/404");

    const playersSearch = async (query) => {
        setLoader(true);
        setHasError(false);
        setLastQuery(query || "");
        try {
            let url = "/api/v1.0/players/";
            const params = { base: baseId };
            if (query && query.length >= 2) params.q = query;
            const res = await api.get(url, { params });
            const results = res.data.results || [];
            const totalCount = res.data.count || results.length || 0;
            setPlayers(results);
            setCurrentPage(res.data.next ? res.data.next.replace(/^https?:\/\/[^/]+/, '') : null);
            setCountPlayers(totalCount);

            const remaining = totalCount - results.length;
            setSkeletonCards(remaining > 0 ? Math.min(10, remaining) : 0);
            setIsSearching(!!(query && query.length >= 3));
        } catch (e) {
            console.error(e);
            setHasError(true);
        } finally {
            setLoader(false);
        }
    };

    const debouncedSearch = useMemo(
        () => debounce(playersSearch, 400),
        []
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    useEffect(() => {
        if (!pageTitle && baseId) {
            api.get(`/api/v1.0/players/base/${baseId}/`)
                .then(res => {
                    setPageTitle(res.data.name || "");
                    setCanWrite(res.data.write === true);
                })
                .catch(() => setPageTitle(""));
        }
    }, [baseId, pageTitle, api]);

    useEffect(() => {
        document.title = t("Players");

        const savedQuery = sessionStorage.getItem(`search_${baseId}`) || "";

        if (savedQuery && savedQuery.length >= 2) {
            playersSearch(savedQuery);
        } else {
            changeCurrentPage();
            getPlayers(`/api/v1.0/players/?base=${baseId}`);
        }
    }, [baseId]);

    useEffect(() => {
        if (inView && currentPage && !loader && !hasError) {
            getPlayers();
        }
    }, [inView, currentPage, loader, hasError]);

    const changeCurrentPage = () => {
        setPlayers([]);
        setSkeletonCards(10);
        setHasError(false);
        setCurrentPage(`/api/v1.0/players/?base=${baseId}`);
    };

    const getPlayers = async () => {
        setLoader(true);
        try {
            const response = await api.get(currentPage || `/api/v1.0/players/?base=${baseId}`);
            const results = response.data.results || [];
            const totalCount = response.data.count || 0;

            if (!countPlayers) {
                setCountPlayers(totalCount);
            }

            setPlayers(prev => [...prev, ...results]);
            setCurrentPage(response.data.next ? response.data.next.replace(/^https?:\/\/[^/]+/, '') : null);

            const updatedCount = players.length + results.length;
            const remaining = totalCount - updatedCount;
            setSkeletonCards(remaining > 0 ? Math.min(10, remaining) : 0);
        } catch (error) {
            console.error(error);
            setHasError(true);
        } finally {
            setLoader(false);
        }
    };

    const handleRetry = () => {
        setHasError(false);
        if (lastQuery && lastQuery.length >= 2) {
            playersSearch(lastQuery);
        } else if (currentPage) {
            getPlayers();
        } else {
            changeCurrentPage();
            getPlayers();
        }
    };

    const handleCreateClick = () => {
        setEditingPlayer(null);
        openModal();
    };

    const handleItemClick = (player) => {
        // history.push(`/admin/bases/${baseId}/players/${player.id}`);
        const params = new URLSearchParams(location.search);
        params.set("player_id", player.id);
        history.push({ search: params.toString() });
    };

    return (
        <Container className="page_container">
            <div className="sticky-toolbar">
                <nav className="tabs-container">
                    <ul className="tabs">
                        <li>
                            <a href="#"
                               onClick={(e) => {
                                   e.preventDefault();
                                   history.push("/admin/bases");
                               }}>
                                <FeatherIcon icon="arrow-left" size={16}/>
                            </a>
                        </li>
                        <li className="easy-black">
                            {pageTitle}
                        </li>

                    </ul>
                </nav>
                <br/>
                <nav className="tabs-container" style={{ paddingBottom: 0, marginTop: "-20px" }}>
                    <ul className="tabs">
                        <li style={{ position: "relative", width: "100%" }}>
                            <FeatherIcon
                                icon="search"
                                size={16}
                                className="search-icon"
                            />
                            <input
                                key={baseId}
                                className="search"
                                type="text"
                                placeholder={t("Players search: name, country, city...")}
                                defaultValue={sessionStorage.getItem(`search_${baseId}`) || ""}
                                onKeyUp={e => {
                                    const val = e.target.value;
                                    sessionStorage.setItem(`search_${baseId}`, val);
                                    debouncedSearch(val);
                                }}
                                style={{ paddingLeft: "30px" }}
                            />
                        </li>
                        {canWrite &&
                            <li className="right">
                                <a href="#" onClick={(e) => {
                                    e.preventDefault();
                                    handleCreateClick();
                                }} className="action-button btn-green">
                                    <FeatherIcon icon="plus"/>
                                    {t("Create")}
                                </a>
                            </li>}
                    </ul>
                </nav>
            </div>
                {compactTable ? (
                    <div className="trows">
                        {players.map((player, idx) => {
                            const subtitle = [
                                player.city,
                                t(getOptionName(COUNTRY_OPTIONS, player.country)),
                                player.level && t(getOptionName(LEVEL_OPTIONS, player.level))
                            ].filter(Boolean).join(", ");
                            return (
                                <CompactTableRow
                                    key={player.id || idx}
                                    variant="entity"
                                    zebra={idx % 2 === 1}
                                    avatarSrc={player.image}
                                    title={<><span className="uppercase">{player.last_name}</span> {player.first_name}</>}
                                    subtitle={subtitle}
                                    onClick={() => handleItemClick(player)}
                                    actions={canWrite && (
                                        <MenuList
                                            deleteEndpoint={`/api/v1.0/players/${player.id}`}
                                            onDeleted={() => setPlayers(prev => prev.filter(p => p.id !== player.id))}
                                            successMessage="Player deleted successfully"
                                            errorMessage="Error deleting player"
                                            is_edit
                                            onEdit={() => {
                                                setEditingPlayer(player);
                                                openModal();
                                            }}
                                            editText={t("Edit profile")}
                                            is_delete={user.is_superuser || player?.creator_id === user?.user_id}
                                            is_merge={user.is_superuser}
                                            onMerge={() => {
                                                setMergePlayer(player);
                                                openMergeModal();
                                            }}
                                            asModal
                                        />
                                    )}
                                />
                            );
                        })}
                        {loader && currentPage && skeletonCards > 0 && (
                            Array.from({length: skeletonCards}).map((_, idx) => (
                                <div className="trow trow-entity" key={`skel-baseplayer-${idx}`} style={{ pointerEvents: "none" }}>
                                    <Skeleton circle width={20} height={20} />
                                    <Skeleton width="60%" height={12} />
                                    <span />
                                </div>
                            ))
                        )}
                    </div>
                ) : (
                <div className="entity-cards">
                    {players.map((player, idx) => (
                        <div className="entity-card" key={player.id || idx} onClick={() => handleItemClick(player)}>
                            {canWrite &&
                                <div className="entity-card-actions" onClick={e => e.stopPropagation()}>
                                    <MenuList
                                        deleteEndpoint={`/api/v1.0/players/${player.id}`}
                                        onDeleted={() => setPlayers(prev => prev.filter(p => p.id !== player.id))}
                                        successMessage="Player deleted successfully"
                                        errorMessage="Error deleting player"
                                        is_edit
                                        onEdit={() => {
                                            setEditingPlayer(player);
                                            openModal();
                                        }}
                                        editText={t("Edit profile")}
                                        is_delete={user.is_superuser || player?.creator_id === user?.user_id}
                                        is_merge={user.is_superuser}
                                        onMerge={() => {
                                            setMergePlayer(player);
                                            openMergeModal();
                                        }}
                                        asModal
                                    />
                                </div>
                            }
                            <div className="entity-card-top">
                                <div className="entity-card-thumb">
                                    {player.image
                                        ? <img src={player.image} alt="" />
                                        : <FeatherIcon icon="user" />}
                                </div>
                                <div className="entity-card-titles">
                                    <div className="entity-card-title"><span className="uppercase">{player.last_name}</span> {player.first_name}</div>
                                    <div className="entity-card-subtitle">
                                        {player.city}
                                        {player.city && player.country && ", "}
                                        {t(getOptionName(COUNTRY_OPTIONS, player.country))}
                                        {(player.city || player.country) && player.level && ", "}
                                        {player.level && t(getOptionName(LEVEL_OPTIONS, player.level))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {loader && currentPage && skeletonCards > 0 && (
                        Array.from({length: skeletonCards}).map((_, idx) => (
                            <Skeleton key={idx} height={82} baseColor="#fff" highlightColor="#f3f3f3" className="entity-card-skeleton" />
                        ))
                    )}
                </div>
                )}

            {!loader && hasError && (
                <div className="not-found">
                    <h2><FeatherIcon icon="alert-triangle" size={106}/></h2>
                    <h4>{t("Something went wrong")}</h4>
                    <p>{t("Failed to load list. Please check your connection and try again.")}</p>
                    <a href="#" className="action-button btn-green" onClick={(e) => { e.preventDefault(); handleRetry(); }}>{t("Try again")}</a>
                </div>
            )}

            <CustomModal isOpen={modalIsOpen} onClose={() => setIsOpen(false)}>
                <PlayerForm
                    baseId={baseId}
                    onClose={() => setIsOpen(false)}
                    playerId={editingPlayer?.id || null}
                    onSuccess={(newItem) => {
                        if (!newItem?.id) {
                            setPlayers(prev => prev.filter(p => p.id !== editingPlayer?.id));
                            setIsOpen(false);
                            return;
                        }
                        // Busted because a replaced photo keeps its filename and
                        // /media/ is cached for 30 days - otherwise the row shows
                        // the old avatar even though the save went through.
                        const saved = newItem.image
                            ? { ...newItem, image: cacheBustUrl(newItem.image) }
                            : newItem;
                        setPlayers(prev => {
                            const index = prev.findIndex(p => p.id === saved.id);
                            if (index !== -1) {
                                const updated = [...prev];
                                updated[index] = saved;
                                return updated;
                            } else {
                                return [saved, ...prev];
                            }
                        });
                        setIsOpen(false);
                    }}
                    img={editingPlayer?.image || null}
                    width={editingPlayer?.width || null}
                    height={editingPlayer?.height || null}
                    canWrite={canWrite}
                />
            </CustomModal>
            <CustomModal isOpen={mergeModalIsOpen} onClose={() => setMergeModalIsOpen(false)}>
                {mergePlayer && (
                    <PlayerMergeForm
                        badPlayer={mergePlayer}
                        baseId={baseId}
                        onClose={() => setMergeModalIsOpen(false)}
                        onSuccess={(deletedId) => {
                            setPlayers(prev => prev.filter(p => p.id !== deletedId));
                        }}
                    />
                )}
            </CustomModal>
            <div ref={ref}></div>
        </Container>
    );
};

export default PlayersList;
